import React, { useState, useEffect, useCallback } from 'react';
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import './StudentGradesView.css';
import './StudentDetailPages.css';

function StudentGradesView({ student }) {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pass, fail
  const [selectedGrade, setSelectedGrade] = useState(null);

  const loadGrades = useCallback(async () => {
    try {
      setLoading(true);
      setGrades([]);

      // Grades live at classes/{c}/assignments/{a}/grades/{g} (iOS) and the
      // top-level `grades` collection — collectionGroup covers both.
      const ids = [student.id, student.studentId].filter(Boolean);
      if (ids.length === 0) {
        setLoading(false);
        return;
      }

      const snapshot = await getDocs(query(
        collectionGroup(db, 'grades'),
        where('studentId', 'in', ids.slice(0, 30))
      ));
      const gradesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by date (newest first)
      gradesData.sort((a, b) => new Date(b.gradedAt) - new Date(a.gradedAt));
      setGrades(gradesData);
    } catch (error) {
      console.error('Error loading grades:', error);
    } finally {
      setLoading(false);
    }
  }, [student.id, student.studentId]);

  useEffect(() => {
    loadGrades();
  }, [loadGrades]);

  const getLetterGrade = (score, maxScore) => {
    if (!score || !maxScore) return 'N/A';
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  };

  const isPassing = (score, maxScore) => {
    if (!score || !maxScore) return false;
    const percentage = (score / maxScore) * 100;
    return percentage >= 60;
  };

  const filteredGrades = grades.filter(grade => {
    if (filter === 'all') return true;
    if (filter === 'pass') return isPassing(grade.score, grade.maxScore);
    if (filter === 'fail') return !isPassing(grade.score, grade.maxScore);
    return true;
  });

  const averageScore = grades.length > 0
    ? grades.reduce((sum, g) => sum + (g.score || 0), 0) / grades.length
    : 0;

  if (selectedGrade) {
    const pct = selectedGrade.score && selectedGrade.maxScore
      ? ((selectedGrade.score / selectedGrade.maxScore) * 100).toFixed(1)
      : 'N/A';
    const letter = getLetterGrade(selectedGrade.score, selectedGrade.maxScore);
    const passing = isPassing(selectedGrade.score, selectedGrade.maxScore);
    console.log('🔥 StudentGrades: Opened grade detail', selectedGrade.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedGrade(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">📊</div>
          <div>
            <h2 className="detail-page-title">
              {selectedGrade.subject || 'Grade'}
              <span className={`detail-badge ${passing ? 'approved' : 'rejected'}`}>{letter}</span>
            </h2>
            <p className="detail-page-subtitle">{selectedGrade.assignmentName || selectedGrade.assignmentTitle || 'Assignment grade'}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Score</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">Score</span><span className="detail-value">{selectedGrade.score ?? 'N/A'} / {selectedGrade.maxScore ?? 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Percentage</span><span className="detail-value">{pct}%</span></div>
            <div className="detail-row"><span className="detail-label">Letter Grade</span><span className="detail-value">{letter}</span></div>
            <div className="detail-row"><span className="detail-label">Result</span><span className="detail-value">{passing ? 'Pass' : 'Fail'}</span></div>
            <div className="detail-row"><span className="detail-label">Graded</span><span className="detail-value">{selectedGrade.gradedAt ? new Date(selectedGrade.gradedAt).toLocaleString() : 'N/A'}</span></div>
            {(selectedGrade.gradedByName || selectedGrade.gradedBy || selectedGrade.teacherName) && (
              <div className="detail-row"><span className="detail-label">Graded By</span><span className="detail-value">{selectedGrade.gradedByName || selectedGrade.teacherName || selectedGrade.gradedBy}</span></div>
            )}
          </div>
        </div>

        {(selectedGrade.feedback || selectedGrade.comments || selectedGrade.teacherComment) && (
          <div className="detail-card">
            <h4>Teacher Feedback</h4>
            <p className="detail-note">{selectedGrade.feedback || selectedGrade.comments || selectedGrade.teacherComment}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="student-grades-view">
      <h2>Grades</h2>

      {loading ? (
        <div className="loading">Loading grades...</div>
      ) : grades.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Grades</h3>
          <p>Your grades will appear here once teachers submit them.</p>
        </div>
      ) : (
        <>
          <div className="summary-card">
            <div className="summary-item">
              <span className="summary-label">Average Score</span>
              <span className="summary-value">{averageScore.toFixed(1)}%</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Grades</span>
              <span className="summary-value">{grades.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Passing</span>
              <span className="summary-value">{grades.filter(g => isPassing(g.score, g.maxScore)).length}</span>
            </div>
          </div>

          <div className="filter-bar">
            <button
              className={`filter-button ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-button ${filter === 'pass' ? 'active' : ''}`}
              onClick={() => setFilter('pass')}
            >
              Pass
            </button>
            <button
              className={`filter-button ${filter === 'fail' ? 'active' : ''}`}
              onClick={() => setFilter('fail')}
            >
              Fail
            </button>
          </div>

          <div className="grades-list">
            {filteredGrades.map(grade => {
              const letterGrade = getLetterGrade(grade.score, grade.maxScore);
              const passing = isPassing(grade.score, grade.maxScore);
              const percentage = grade.score && grade.maxScore
                ? ((grade.score / grade.maxScore) * 100).toFixed(1)
                : 'N/A';
              return (
                <div key={grade.id} className="grade-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedGrade(grade)}>
                  <div className="grade-header">
                    <h3>{grade.subject || 'Subject'}</h3>
                    <span className={`letter-grade ${passing ? 'pass' : 'fail'}`}>
                      {letterGrade}
                    </span>
                  </div>
                  <div className="grade-details">
                    <span className="score">{grade.score || 0} / {grade.maxScore || 0}</span>
                    <span className="percentage">{percentage}%</span>
                  </div>
                  {grade.gradedAt && (
                    <p className="graded-date">Graded: {new Date(grade.gradedAt).toLocaleDateString()}</p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default StudentGradesView;
